import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useProducts } from "@/common/hooks/queries/useProducts";
import useDebounce from "@/common/hooks/useDebounce";
import { geosMap } from "@/common/constants/geosMap.constant";
import { typeStrategy } from "@/common/constants/typeStrategy.constant";
import { Mapper } from "@/common/services/mapper";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { ProductData } from "@/common/types/product.type";
import { useAnpPrices } from "../../hooks/useAnpPrices";
import { AnpPricesTransformer } from "../../services/anpPricesTransformer";
import { anpPricesByGeoTypeColumns } from "../../constants/anpPricesDefaultColumns.constant";
import { defaultMeta } from "@/common/constants/defaultMeta.constant";
import { useGeoOptionsByType } from "@/common/hooks/queries/useGeoOptionsByType";

interface AnpPricesTableProps {
  reportId?: string;
  geoType: keyof typeof typeStrategy;
}

const AnpPricesTable = ({ reportId, geoType }: AnpPricesTableProps) => {
  const { filterParams, setFilterParams } = useFilterParams();

  const geoTypeValue = geosMap.find((geo) => geo.type === geoType)?.value;

  const [searchGeo, setSearchGeo] = useDebounce('');
  const [searchProduct, setSearchProduct] = useDebounce('');

  const { data: productOptions = [] } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const { data: geoOptions = [] } = useGeoOptionsByType({
    filters: {
      type: geoTypeValue,
      search: searchGeo,
    },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  });

  const {
    data: anpPrices = [],
    meta = defaultMeta,
    isFetching: anpPricesIsFetching,
    error: anpPricesError = {} as any,
    refetch: refetchAnpPrices,
  } = useAnpPrices({
    filters: {
      reportId,
      productId: filterParams.product?.value,
      geoType,
      geoCityId: geoType === 'Geo::City' ? filterParams.cities?.value : '',
      geoStateId: geoType === 'Geo::State' ? filterParams.states?.value : '',
      geoRegionId: geoType === 'Geo::Region' ? filterParams.regions?.value : '',
      page: Number(filterParams.page) || 1,
    },
    mapper: Mapper.mapWithTransformer({ transformer: AnpPricesTransformer.tableTransformer(geoType) }),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-340px)]"
      statusCode={anpPricesError?.status}
      refetchFunction={refetchAnpPrices}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Produto',
                  options: [{ label: 'Todos', value: '' }, ...productOptions],
                  onChange: (product) => setFilterParams((prev: any) => ({ ...prev, product })),
                  onInputChange: setSearchProduct,
                  value: filterParams.product,
                },
                {
                  className: geoType === 'Geo::Country' ? 'hidden' : 'min-w-40',
                  placeholder: typeStrategy[geoType],
                  options: [{ label: 'Todos', value: '' }, ...geoOptions],
                  onChange: (option) => geoTypeValue && setFilterParams((prev: any) => ({ ...prev, [geoTypeValue]: option })),
                  onInputChange: setSearchGeo,
                  value: geoTypeValue ? filterParams[geoTypeValue] : undefined,
                },
              ]}
            />
          </div>
        </div>

        <div>
          <Table
            className="max-h-[calc(100vh-276px-7.5rem)]"
            columns={anpPricesByGeoTypeColumns[geoType]}
            rows={anpPrices}
            isLoadingData={anpPricesIsFetching}
            hasPagination
            rowEmptyValue="--"
            totalCount={meta.total}
            totalPages={meta.total_pages}
            page={Number(filterParams.page) || 1}
            setPage={(page) => setFilterParams((prev: any) => ({ ...prev, page }))}
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  );
};

export default AnpPricesTable;
